/**
 * HIPAA Session Timeout Hook
 * 
 * Implements automatic session timeout per HIPAA requirements
 * Monitors user activity and automatically logs out inactive users
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { useAzureAuth } from './useAzureAuth';
import { SESSION_CONFIG } from '@/config/hipaaSecurityConfig';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';

// Cross-tab session activity channel
const SESSION_ACTIVITY_CHANNEL = 'oneview-session-activity';
let sessionActivityChannel: BroadcastChannel | null = null;

const getSessionActivityChannel = (): BroadcastChannel | null => {
  if (typeof BroadcastChannel === 'undefined') {
    return null;
  }
  if (!sessionActivityChannel) {
    sessionActivityChannel = new BroadcastChannel(SESSION_ACTIVITY_CHANNEL);
  }
  return sessionActivityChannel;
};

interface UseSessionTimeoutOptions {
  onTimeout?: () => void;
  onWarning?: () => void;
}

export const useSessionTimeout = (options?: UseSessionTimeoutOptions) => {
  const { logout } = useAzureAuth();
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number>(SESSION_CONFIG.idleTimeoutMs);
  
  const timeoutRef = useRef<number | undefined>(undefined);
  const warningTimeoutRef = useRef<number | undefined>(undefined);
  const lastActivityRef = useRef<number>(Date.now());

  /**
   * Handle session timeout
   */
  const handleTimeout = useCallback(async () => {
    // If user is not authenticated anymore, do nothing
    if (!isAuthenticated || !user) {
      return;
    }

    // Clear timeouts
    if (timeoutRef.current !== undefined) window.clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current !== undefined) window.clearTimeout(warningTimeoutRef.current);

    // Call custom timeout handler if provided
    options?.onTimeout?.();

    // Logout user with timeout reason
    await logout('SESSION_TIMEOUT');
  }, [logout, user, isAuthenticated, options]);

  /**
   * Show warning before timeout
   */
  const handleWarning = useCallback(() => {
    setShowWarning(true);
    options?.onWarning?.();
  }, [options]);

  /**
   * Reset session timeout
   */
  const resetTimeout = useCallback(() => {
    // Only manage timeouts for authenticated users
    if (!isAuthenticated) {
      // Ensure we clear any existing timers when user logs out
      if (timeoutRef.current !== undefined) window.clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current !== undefined) window.clearTimeout(warningTimeoutRef.current);
      setShowWarning(false);
      return;
    }

    // Clear existing timeouts
    if (timeoutRef.current !== undefined) window.clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current !== undefined) window.clearTimeout(warningTimeoutRef.current);

    // Reset warning state
    setShowWarning(false);

    const now = Date.now();
    lastActivityRef.current = now;
    setTimeRemaining(SESSION_CONFIG.idleTimeoutMs);

    // Broadcast activity to other tabs (if supported)
    try {
      const channel = getSessionActivityChannel();
      channel?.postMessage({ type: 'ACTIVITY', timestamp: now });
    } catch (e) {
      // If BroadcastChannel fails, continue with local timers only
      console.warn('Failed to broadcast session activity', e);
    }

    // Set warning timeout
    warningTimeoutRef.current = window.setTimeout(() => {
      handleWarning();
    }, SESSION_CONFIG.warningTimeoutMs);

    // Set logout timeout
    timeoutRef.current = window.setTimeout(() => {
      handleTimeout();
    }, SESSION_CONFIG.idleTimeoutMs);
  }, [handleTimeout, handleWarning, isAuthenticated]);

  /**
   * Track user activity & manage timers based on auth state (this tab)
   */
  useEffect(() => {
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    if (isAuthenticated) {
      const resetTimer = () => {
        resetTimeout();
      };

      // Attach listeners
      events.forEach(event => {
        document.addEventListener(event, resetTimer);
      });

      // Initialize timeout when user becomes authenticated
      resetTimeout();
    }

    return () => {
      // Always remove listeners on cleanup
      events.forEach(event => {
        document.removeEventListener(event, resetTimeout);
      });

      // Clear timers when component unmounts or deps change
      if (timeoutRef.current !== undefined) window.clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current !== undefined) window.clearTimeout(warningTimeoutRef.current);
    };
  }, [resetTimeout, isAuthenticated]);

  /**
   * Listen for activity broadcasts from other tabs using BroadcastChannel
   * so activity in one tab keeps sessions alive in all tabs.
   */
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const channel = getSessionActivityChannel();
    if (!channel) {
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; timestamp?: number };
      if (!data || data.type !== 'ACTIVITY' || typeof data.timestamp !== 'number') {
        return;
      }

      const newTs = data.timestamp;
      // Ignore stale or same-timestamp messages
      if (newTs <= lastActivityRef.current) {
        return;
      }

      lastActivityRef.current = newTs;

      // Recalculate remaining time and reset timers in this tab
      const now = Date.now();
      const elapsed = now - newTs;
      const remaining = Math.max(0, SESSION_CONFIG.idleTimeoutMs - elapsed);

      if (timeoutRef.current !== undefined) window.clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current !== undefined) window.clearTimeout(warningTimeoutRef.current);

      setShowWarning(false);
      setTimeRemaining(remaining);

      if (remaining <= 0) {
        // Already timed out based on shared activity timeline
        handleTimeout();
        return;
      }

      const warningIn = Math.max(
        0,
        remaining - (SESSION_CONFIG.idleTimeoutMs - SESSION_CONFIG.warningTimeoutMs)
      );

      warningTimeoutRef.current = window.setTimeout(() => {
        handleWarning();
      }, warningIn);

      timeoutRef.current = window.setTimeout(() => {
        handleTimeout();
      }, remaining);
    };

    channel.onmessage = handleMessage;

    return () => {
      // Only clear handler; channel itself is shared singleton
      if (channel) {
        channel.onmessage = null;
      }
    };
  }, [isAuthenticated, handleTimeout, handleWarning]);

  /**
   * Countdown timer while warning is visible
   */
  useEffect(() => {
    if (!showWarning) {
      return;
    }

    const interval = window.setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      const remaining = SESSION_CONFIG.idleTimeoutMs - elapsed;
      setTimeRemaining(Math.max(0, remaining));
    }, 1000);

    return () => {
      window.clearInterval(interval);
    };
  }, [showWarning]);

  /**
   * Extend session (called when user clicks "Stay logged in")
   */
  const extendSession = useCallback(() => {
    resetTimeout();
  }, [resetTimeout]);

  return {
    showWarning,
    timeRemaining: Math.floor(timeRemaining / 1000), // Return in seconds
    extendSession,
    resetTimeout,
  };
};

